import { fetchJSON as fetchAPI } from './client';

export type MissionOutcome = {
  key: string;
  name: string;
  direction: string;
};

export type PhilosophyQuestion = {
  key: string;
  text: string;
  order: number;
};

export type NorthStarMetric = {
  key: string;
  name: string;
  category: string;
  description?: string;
  unit?: string;
};

export type ProductPillar = {
  id: number;
  key: string;
  name: string;
  goal: string;
  description?: string;
};

export type AIPrincipleRule = {
  key: string;
  name: string;
  description: string;
  mandatory: boolean;
};

export type RecommendationField = {
  key: string;
  name: string;
  description: string;
};

export type ExperimentStage = {
  key: string;
  name: string;
  order: number;
  description?: string;
};

export type DNAComponent = {
  key: string;
  name: string;
  score?: number;
  description?: string;
};

export type AttributionFactor = {
  key: string;
  name: string;
  impact_pct?: number;
  description?: string;
};

export type LifecycleStage = {
  key: string;
  name: string;
  order: number;
};

export type KnowledgeGraphNode = {
  key: string;
  name: string;
  connections?: string[];
};

export type HealthScoreDimension = {
  key: string;
  name: string;
  current_value: number;
  target_value: number;
  gap: number;
  priority: string;
  recommended_actions?: string[];
};

export type UXPrinciple = {
  key: string;
  question: string;
  description?: string;
};

export type SuccessMeasurementTemplate = {
  required_fields: string[];
  description: string;
};

export type MarketplaceCoverage = {
  key: string;
  name: string;
};

export type FeatureAlignmentEvaluation = {
  feature_name: string;
  aligned: boolean;
  target_kpi?: string;
  pillar?: string;
  gaps?: string[];
  evaluated_at: string;
};

export type ExtensionPoint = {
  pillar_key: string;
  name: string;
  description: string;
};

export type BibleDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  pillars: number;
  north_star_metrics: number;
  marketplaces: number;
  avg_health_score: number;
  active_experiments: number;
  open_recommendations: number;
  marketplaces_list: MarketplaceCoverage[];
  extension_points: ExtensionPoint[];
  generated_at: string;
};

export type BibleStatus = {
  bible_id: string;
  version: string;
  pillars: number;
  north_star_metrics: number;
  marketplaces: number;
  avg_health_score: number;
  active_experiments: number;
  open_recommendations: number;
  mandatory: boolean;
  status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const marketplaceIntelligenceBibleApi = {
  dashboard: () => fetchMarketIntel<BibleDashboard>('/bible/dashboard'),
  mission: () => fetchMarketIntel<{ outcomes: MissionOutcome[] }>('/bible/mission').then((r) => r.outcomes),
  philosophy: () => fetchMarketIntel<{ questions: PhilosophyQuestion[] }>('/bible/philosophy').then((r) => r.questions),
  northStarMetrics: () => fetchMarketIntel<{ metrics: NorthStarMetric[] }>('/bible/north-star-metrics').then((r) => r.metrics),
  pillars: () => fetchMarketIntel<{ pillars: ProductPillar[] }>('/bible/pillars').then((r) => r.pillars),
  aiPrinciples: () => fetchMarketIntel<{ principles: AIPrincipleRule[]; recommendation_fields: RecommendationField[] }>('/bible/ai-principles'),
  experimentLifecycle: () => fetchMarketIntel<{ stages: ExperimentStage[] }>('/bible/experiment-lifecycle').then((r) => r.stages),
  marketplaceDNA: () => fetchMarketIntel<{ components: DNAComponent[] }>('/bible/marketplace-dna').then((r) => r.components),
  attribution: () => fetchMarketIntel<{ factors: AttributionFactor[] }>('/bible/attribution').then((r) => r.factors),
  recommendationLifecycle: () => fetchMarketIntel<{ stages: LifecycleStage[] }>('/bible/recommendation-lifecycle').then((r) => r.stages),
  knowledgeGraph: () => fetchMarketIntel<{ nodes: KnowledgeGraphNode[] }>('/bible/knowledge-graph').then((r) => r.nodes),
  healthScore: () => fetchMarketIntel<{ dimensions: HealthScoreDimension[]; avg_score: number }>('/bible/health-score'),
  uxPrinciples: () => fetchMarketIntel<{ principles: UXPrinciple[] }>('/bible/ux-principles').then((r) => r.principles),
  successMeasurement: () => fetchMarketIntel<SuccessMeasurementTemplate>('/bible/success-measurement'),
  marketplaces: () => fetchMarketIntel<{ marketplaces: MarketplaceCoverage[] }>('/bible/marketplaces').then((r) => r.marketplaces),
  evaluateFeature: (body: { feature_name: string; target_kpi: string; pillar_key: string }) =>
    fetchMarketIntel<FeatureAlignmentEvaluation>('/bible/evaluate-feature', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  status: () => fetchMarketIntel<BibleStatus>('/bible/status'),
  extensionPoints: () => fetchMarketIntel<{ extension_points: ExtensionPoint[] }>('/bible/extensions').then((r) => r.extension_points),
};
